import { AppLayout, Spinner } from "@amzn/awsui-components-react";
import { Hub } from "aws-amplify";
import * as React from "react";
import { useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { HashRouter, Route, Routes } from "react-router-dom";
import { SpoofIdentityBanner } from "./components/common/SpoofIdentityBanner";
import { AskLegalLayout } from "./components/layout/AskLegalLayout";
import { InstanceDataProvider } from "./contexts/InstanceDataContext";
import { TitleProvider } from "./contexts/TitleContext";
import { commonRoutes } from "./pages/common";
import { decisionTreeRoutes } from "./pages/decision-tree";
import { instanceRoutes } from "./pages/instance";
import { legalContactRoutes } from "./pages/legal-contact";
import { pageRoutes } from "./pages/page";
import { pageLibraryRoutes } from "./pages/page-library";
import { recommendationRoutes } from "./pages/recommendation";
import { redirectRoutes } from "./pages/redirects";
import { searchRoutes } from "./pages/search";
import { sharedPageRoutes } from "./pages/shared-page";
import { devopsRoutes } from "./pages/devops";
import { AppContext } from "./setup/context";
import { extractInstanceFromParams } from "./setup/useRoutedParamsWithContext";
import "./styles/component/layout/AskLegalLayout.scss";
import { sleep } from "./utils/common-utils";
import * as Cookies from "js-cookie";
import { QuickAgentChatPanelWrapper } from "./components/quick-agent-chat-panel/QuickAgentChatPanelWrapper";
import { QuickAgentChatPanelWrapperV2 } from "./components/quick-agent-chat-panel/QuickAgentChatPanelWrapperV2";
import { Preference } from "./setup/preference";

const ORIGINAL_URL_STORAGE_KEY = "OrignalURLHashBeforeAuth";
const REDIRECT_COOKIE_NAME = "pf_redirect_hash";
const REDIRECT_COOKIE_MAX_AGE_MINUTES = 5;
const AUTH_TOKEN_WAIT_MS = 500;

export interface RouteDefinition {
  path: string;
  content: (() => JSX.Element) | JSX.Element;
  header: () => JSX.Element;
  footer?: () => JSX.Element;
  breadcrumbs?: () => JSX.Element;
  contentClass?: string;
}
enum AuthStatus {
  Authenticated,
  UnAuthenticated,
  Error
}

const routes: RouteDefinition[] = [
  ...commonRoutes,
  ...instanceRoutes,
  ...pageLibraryRoutes,
  ...pageRoutes,
  ...searchRoutes,
  ...recommendationRoutes,
  ...decisionTreeRoutes,
  ...legalContactRoutes,
  ...devopsRoutes,
  ...sharedPageRoutes,
  ...redirectRoutes
];

const isOAuthCallback = (url: string): boolean => {
  const search = new URL(url).search;
  const hash = new URL(url).hash;
  return search.includes("code=") || search.includes("access_token=") ||
    hash.includes("code=") || hash.includes("access_token=");
};

const isDefaultRoute = (hash: string): boolean =>
  !hash || hash === "#/" || hash === "#" || hash === "#/home";

// A hash-router deep link, e.g. "#/page/<id>/live". Used to distinguish a real in-app
// destination from an empty/default hash or an absolute URL.
const isHashRoute = (route: string | null | undefined): route is string =>
  !!route && route.startsWith("#/");

export const App = () => {
  const context = React.useContext(AppContext);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.UnAuthenticated);

  const extractInstanceFromRoute = (route: string): string | null => {
    try {
      const path = route.replace(/^#/, "");
      const directInstanceRoutes = ["instance", "search", "legal-contact", "recommendation"];
      const directInstancePattern = new RegExp(`^\\/(${directInstanceRoutes.join("|")})\\/([^/]+)`);
      const directMatch = path.match(directInstancePattern);
      const indirectRoutes = {
        "page": "pageId",
        "page-library": "pageLibraryId",
        "decision-tree": "containerId"
      };

      const params: Record<string, string | undefined> = {};
      if (directMatch) {
        params.instanceId = directMatch[2];
      } else {
        for (const [routeType, paramName] of Object.entries(indirectRoutes)) {
          const match = path.match(new RegExp(`^\\/${routeType}\\/([^/]+)`));
          if (match) {
            params[paramName] = match[1];
            break;
          }
        }
      }

      return extractInstanceFromParams(params);
    } catch (e) {
      console.warn("Error extracting instance from route:", route, e);
      return null;
    }
  };

  const autoSetInstancePreference = async (preferredHash?: string): Promise<void> => {
    try {
      // Prefer the user's intended deep link (captured before the OAuth redirect) over
      // window.location.hash, which at OAuth-callback time no longer reflects the target page.
      const routeToInspect = isHashRoute(preferredHash) ? preferredHash : window.location.hash;
      const extractedInstanceId = extractInstanceFromRoute(routeToInspect);
      if (extractedInstanceId) {
        const identity = await context.getIdentity();
        const currentPreference = context.getPreference();
        if (!currentPreference || currentPreference.userId !== identity.id || !currentPreference.instanceId) {
          await context.setPreference(extractedInstanceId);
        }
      }
    } catch (e) {
      console.warn("Error auto-setting instance preference:", e);
    }
  };

  const handleSignIn = () => onSignedIn();
  const handleSignInFailure = (message: string) => {
    console.debug(`Sign-in failed: ${message}`);
    setAuthStatus(AuthStatus.Error);
  };

  const onSignedIn = async (): Promise<void> => {
    await context.getIdentity();
    // Read the intended deep link BEFORE setting the instance preference so the preference is
    // derived from where the user actually wanted to go, not the post-OAuth callback hash.
    const storedRoute = Cookies.get(REDIRECT_COOKIE_NAME) || localStorage.getItem(ORIGINAL_URL_STORAGE_KEY);
    await autoSetInstancePreference(isHashRoute(storedRoute) ? storedRoute : undefined);
    if (storedRoute) {
      localStorage.removeItem(ORIGINAL_URL_STORAGE_KEY);
      Cookies.remove(REDIRECT_COOKIE_NAME, { path: "/" });
      if (isHashRoute(storedRoute)) {
        // Restore the deep link before the app renders. HashRouter is not mounted until
        // setAuthStatus(Authenticated) below causes a re-render, so it will read this hash on
        // first mount and land on the intended page instead of ApplicationDefaultHome.
        window.history.replaceState(null, "", `${window.location.pathname}${storedRoute}`);
      }
    }
    setAuthStatus(AuthStatus.Authenticated);
  };

  const initializeAuth = async (): Promise<void> => {
    try {
      Hub.listen("auth", (data) => {
        switch (data.payload.event) {
          case "signIn":
            handleSignIn();
            break;
          case "signIn_failure":
            handleSignInFailure(data.payload.message);
            break;
          case "customOAuthState":
            // Restore deep link hash from OAuth state (survives AEA browser context switch)
            const returnedState = data.payload.data;
            if (isHashRoute(returnedState)) {
              localStorage.setItem(ORIGINAL_URL_STORAGE_KEY, returnedState);
            }
            break;
        }
      });
      await context.init();
      await sleep(AUTH_TOKEN_WAIT_MS); // Wait for auth tokens
      await context.currentUser();


      await onSignedIn();
    } catch (err) {
      console.warn(err);
      const currentUrl = window.location.href;
      const currentHash = window.location.hash;
      const hasStoredUrl = localStorage.getItem(ORIGINAL_URL_STORAGE_KEY);
      if (!isOAuthCallback(currentUrl) && !hasStoredUrl && !isDefaultRoute(currentHash)) {
        Cookies.set(REDIRECT_COOKIE_NAME, currentHash, {
          expires: REDIRECT_COOKIE_MAX_AGE_MINUTES / (24 * 60),
          path: "/",
          sameSite: "Lax",
          secure: true
        });
        localStorage.setItem(ORIGINAL_URL_STORAGE_KEY, currentHash);
      }
      if (!isOAuthCallback(currentUrl)) {
        await context.signIn(isDefaultRoute(currentHash) ? undefined : currentHash);
      }
    }
  };

  const renderRoutes = () => (
    <Routes>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <div className="awsui">
              <AskLegalLayout
                header={<route.header />}
                content={
                  <div className={route.contentClass || "sherpa-content"}>
                    {route.breadcrumbs && <route.breadcrumbs />}
                    {typeof route.content === "function" ? route.content() : route.content}
                  </div>
                }
                footer={route.footer ? <route.footer /> : null}
              />
            </div>
          }
        />
      ))}
    </Routes>
  );

  useEffect(() => {
    initializeAuth();
  }, []);

  if (authStatus === AuthStatus.UnAuthenticated) {
    return (
      <span className="awsui-util-status-inactive vertical-center horizontal-center" style={{ paddingTop: "25px" }}>
        <Spinner size="large" /> Loading Resources
      </span>
    );
  }
  if (authStatus === AuthStatus.Error) {
    return (
      <div>
        Failed to load. Please try to refresh the page. If the issue still persists after multiple tries,
        please contact altar-dev@amazon.com for support.
      </div>
    );
  }

  return (
    <CookiesProvider>
      <TitleProvider>
        <AppContext.Provider value={context}>
          <AppLayout
            toolsHide
            navigationHide
            disableContentPaddings
            content={
              <>
                <SpoofIdentityBanner />
                <InstanceDataProvider>
                  <HashRouter>{renderRoutes()}</HashRouter>
                </InstanceDataProvider>
                {/* NOTE: QuickAgentChatPanelWrapper is intentionally rendered OUTSIDE the HashRouter.
                    This prevents the chat iframe from remounting (and losing conversation state) on route
                    changes. Route detection relies on monkey-patched pushState/replaceState in useRouteType
                    (HashRouter uses these internally and they don't fire native events). */}
                {context.hasLab(Preference.Lab.QuickChatAgent) && context.config?.quickAgentChat?.url && (
                  context.hasLab(Preference.Lab.AgenticUI)
                    ? <QuickAgentChatPanelWrapperV2 agentUrl={context.config.quickAgentChat.url} />
                    : <QuickAgentChatPanelWrapper agentUrl={context.config.quickAgentChat.url} />
                )}
              </>
            }
          />
        </AppContext.Provider>
      </TitleProvider>
    </CookiesProvider>
  );
};
